/**
 * QuickFilters Component
 * Horizontal chip-based filters for quickly filtering opportunities by urgency and set-aside type.
 * Inspired by professional government contracting platforms like GovDash.
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { AlertTriangle, Bookmark, Clock, Grid3X3, List, RotateCcw, Shield, X } from 'lucide-react';
function FilterChip({ active, onClick, icon, children, variant = 'default', disabled, suffix, testid }) {
    // Neutral chip by default; the active state carries the accent. Semantic
    // tokens theme themselves (no dark: variants).
    const inactive = 'bg-card text-muted-foreground border-border hover:bg-muted hover:text-foreground';
    const variantStyles = {
        default: active ? 'bg-primary/10 text-primary border-primary/30' : inactive,
        urgent: active ? 'bg-destructive/10 text-destructive border-destructive/30' : inactive,
        warning: active ? 'bg-warning/10 text-warning border-warning/30' : inactive,
        success: active ? 'bg-success/10 text-success border-success/30' : inactive
    };
    return /*#__PURE__*/ _jsxs("button", {
        type: "button",
        onClick: disabled ? undefined : onClick,
        disabled: disabled,
        "data-testid": testid,
        className: `
        inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium
        border transition-all duration-150
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${variantStyles[variant]}
      `,
        children: [
            icon,
            children,
            suffix
        ]
    });
}
const SET_ASIDE_OPTIONS = [
    {
        id: 'Small Business',
        label: 'Small Business',
        shortLabel: 'SB'
    },
    {
        id: '8(a)',
        label: '8(a)'
    },
    {
        id: 'HUBZone',
        label: 'HUBZone'
    },
    {
        id: 'SDVOSB',
        label: 'SDVOSB'
    },
    {
        id: 'WOSB',
        label: 'WOSB'
    }
];
export function QuickFilters({ filters, onFilterChange, viewMode, onViewModeChange, totalCount, filteredCount, dialogFilterCount = 0, onClearAll, onClearDialogFilters }) {
    const hasQuickFilters = filters.urgency !== 'all' || filters.setAside !== null || filters.source !== null || filters.saved;
    // Active = any quick chip OR any dialog filter, so the bar surfaces and can
    // clear dialog filters too (not just chips).
    const hasActiveFilters = hasQuickFilters || dialogFilterCount > 0;
    const showingFiltered = filteredCount !== undefined && filteredCount !== totalCount;
    const toggleUrgency = (urgency)=>{
        onFilterChange({
            ...filters,
            urgency: filters.urgency === urgency ? 'all' : urgency
        });
    };
    const toggleSetAside = (setAside)=>{
        onFilterChange({
            ...filters,
            setAside: filters.setAside === setAside ? null : setAside
        });
    };
    const toggleSource = (source)=>{
        onFilterChange({
            ...filters,
            source: filters.source === source ? null : source
        });
    };
    const toggleSaved = ()=>{
        onFilterChange({
            ...filters,
            saved: !filters.saved
        });
    };
    // Clears both the quick chips and the dialog filters so nothing is left
    // silently applied.
    const clearFilters = ()=>{
        onClearAll();
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center justify-between gap-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-xs font-medium text-muted-foreground uppercase tracking-wide mr-1",
                                children: "Deadline"
                            }),
                            /*#__PURE__*/ _jsx(FilterChip, {
                                active: filters.urgency === 'urgent',
                                onClick: ()=>toggleUrgency('urgent'),
                                icon: /*#__PURE__*/ _jsx(AlertTriangle, {
                                    className: "h-3.5 w-3.5"
                                }),
                                variant: "urgent",
                                testid: Testids.opportunities.deadlineUrgentChip,
                                children: "Due in 7 days"
                            }),
                            /*#__PURE__*/ _jsx(FilterChip, {
                                active: filters.urgency === 'soon',
                                onClick: ()=>toggleUrgency('soon'),
                                icon: /*#__PURE__*/ _jsx(Clock, {
                                    className: "h-3.5 w-3.5"
                                }),
                                variant: "warning",
                                testid: Testids.opportunities.deadlineSoonChip,
                                children: "Due in 30 days"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "h-6 w-px bg-border mx-2"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-xs font-medium text-muted-foreground uppercase tracking-wide mr-1",
                                children: "Set-Aside"
                            }),
                            SET_ASIDE_OPTIONS.map((option)=>/*#__PURE__*/ _jsx(FilterChip, {
                                    active: filters.setAside === option.id,
                                    onClick: ()=>toggleSetAside(option.id),
                                    icon: filters.setAside === option.id ? /*#__PURE__*/ _jsx(Shield, {
                                        className: "h-3.5 w-3.5"
                                    }) : undefined,
                                    variant: "success",
                                    children: option.shortLabel || option.label
                                }, option.id)),
                            /*#__PURE__*/ _jsx("div", {
                                className: "h-6 w-px bg-border mx-2"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-xs font-medium text-muted-foreground uppercase tracking-wide mr-1",
                                children: "Source"
                            }),
                            /*#__PURE__*/ _jsx(FilterChip, {
                                active: filters.source === 'sam-api-v2',
                                onClick: ()=>toggleSource('sam-api-v2'),
                                icon: /*#__PURE__*/ _jsx("span", {
                                    className: "h-1.5 w-1.5 rounded-full bg-primary"
                                }),
                                children: "SAM.gov"
                            }),
                            /*#__PURE__*/ _jsx(FilterChip, {
                                active: false,
                                onClick: ()=>{},
                                disabled: true,
                                suffix: /*#__PURE__*/ _jsx("span", {
                                    className: "text-[10px] font-normal opacity-70",
                                    children: "Coming soon"
                                }),
                                children: "eBuy"
                            }),
                            /*#__PURE__*/ _jsx(FilterChip, {
                                active: false,
                                onClick: ()=>{},
                                disabled: true,
                                suffix: /*#__PURE__*/ _jsx("span", {
                                    className: "text-[10px] font-normal opacity-70",
                                    children: "Coming soon"
                                }),
                                children: "T4NG"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "h-6 w-px bg-border mx-2"
                            }),
                            /*#__PURE__*/ _jsx(FilterChip, {
                                active: filters.saved,
                                onClick: toggleSaved,
                                icon: /*#__PURE__*/ _jsx(Bookmark, {
                                    className: `h-3.5 w-3.5 ${filters.saved ? 'fill-current' : ''}`
                                }),
                                variant: "default",
                                children: "Saved Only"
                            }),
                            hasActiveFilters && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "h-6 w-px bg-border mx-2"
                                    }),
                                    /*#__PURE__*/ _jsxs("button", {
                                        type: "button",
                                        onClick: clearFilters,
                                        className: "inline-flex items-center gap-1 px-2 py-1 text-sm text-muted-foreground hover:text-foreground transition-colors",
                                        children: [
                                            /*#__PURE__*/ _jsx(RotateCcw, {
                                                className: "h-3.5 w-3.5"
                                            }),
                                            "Clear"
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-3",
                        children: [
                            showingFiltered ? /*#__PURE__*/ _jsxs("span", {
                                className: "text-sm text-muted-foreground",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "font-semibold text-foreground",
                                        children: filteredCount
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "mx-1",
                                        children: "of"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        children: totalCount.toLocaleString()
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsxs("span", {
                                className: "text-sm text-muted-foreground",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "font-semibold text-foreground",
                                        children: totalCount.toLocaleString()
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "ml-1",
                                        children: "opportunities"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex rounded-lg border border-border p-1 bg-muted",
                                children: [
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        onClick: ()=>onViewModeChange('list'),
                                        className: `p-1.5 rounded transition-colors ${viewMode === 'list' ? 'bg-background text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'}`,
                                        title: "List view",
                                        "aria-label": "List view",
                                        children: /*#__PURE__*/ _jsx(List, {
                                            className: "h-4 w-4"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        onClick: ()=>onViewModeChange('grid'),
                                        className: `p-1.5 rounded transition-colors ${viewMode === 'grid' ? 'bg-background text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'}`,
                                        title: "Grid view",
                                        "aria-label": "Grid view",
                                        children: /*#__PURE__*/ _jsx(Grid3X3, {
                                            className: "h-4 w-4"
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            hasActiveFilters && /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 text-sm",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-muted-foreground",
                        children: "Active filters:"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap gap-1.5",
                        children: [
                            filters.urgency === 'urgent' && /*#__PURE__*/ _jsxs("span", {
                                className: "inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-destructive/10 text-destructive text-xs font-medium",
                                children: [
                                    "Due in 7 days",
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        onClick: ()=>onFilterChange({
                                                ...filters,
                                                urgency: 'all'
                                            }),
                                        className: "hover:text-destructive",
                                        "aria-label": "Remove urgency filter",
                                        children: /*#__PURE__*/ _jsx(X, {
                                            className: "h-3 w-3"
                                        })
                                    })
                                ]
                            }),
                            filters.urgency === 'soon' && /*#__PURE__*/ _jsxs("span", {
                                className: "inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-warning/10 text-warning text-xs font-medium",
                                children: [
                                    "Due in 30 days",
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        onClick: ()=>onFilterChange({
                                                ...filters,
                                                urgency: 'all'
                                            }),
                                        className: "hover:text-warning",
                                        "aria-label": "Remove urgency filter",
                                        children: /*#__PURE__*/ _jsx(X, {
                                            className: "h-3 w-3"
                                        })
                                    })
                                ]
                            }),
                            filters.setAside && /*#__PURE__*/ _jsxs("span", {
                                className: "inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-success/10 text-success text-xs font-medium",
                                children: [
                                    filters.setAside,
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        onClick: ()=>onFilterChange({
                                                ...filters,
                                                setAside: null
                                            }),
                                        className: "hover:text-success",
                                        "aria-label": "Remove set-aside filter",
                                        children: /*#__PURE__*/ _jsx(X, {
                                            className: "h-3 w-3"
                                        })
                                    })
                                ]
                            }),
                            filters.source && /*#__PURE__*/ _jsxs("span", {
                                className: "inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium",
                                children: [
                                    filters.source === 'sam-api-v2' ? 'SAM.gov' : filters.source,
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        onClick: ()=>onFilterChange({
                                                ...filters,
                                                source: null
                                            }),
                                        className: "hover:text-primary",
                                        "aria-label": "Remove source filter",
                                        children: /*#__PURE__*/ _jsx(X, {
                                            className: "h-3 w-3"
                                        })
                                    })
                                ]
                            }),
                            filters.saved && /*#__PURE__*/ _jsxs("span", {
                                className: "inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium",
                                children: [
                                    "Saved Only",
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        onClick: ()=>onFilterChange({
                                                ...filters,
                                                saved: false
                                            }),
                                        className: "hover:text-primary",
                                        "aria-label": "Remove saved filter",
                                        children: /*#__PURE__*/ _jsx(X, {
                                            className: "h-3 w-3"
                                        })
                                    })
                                ]
                            }),
                            dialogFilterCount > 0 && /*#__PURE__*/ _jsxs("span", {
                                className: "inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium",
                                children: [
                                    dialogFilterCount,
                                    " advanced ",
                                    dialogFilterCount === 1 ? 'filter' : 'filters',
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        onClick: onClearDialogFilters,
                                        className: "hover:text-primary",
                                        "aria-label": "Remove advanced filters",
                                        children: /*#__PURE__*/ _jsx(X, {
                                            className: "h-3 w-3"
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
